import React from 'react';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { LearningMode } from 'app/types';
import { modeById, slivWord } from 'pages/Learning/learningMeta';
import LearningExerciseChips from 'pages/Learning/components/LearningExerciseChips';
import LearningGoalRing from 'pages/Learning/components/LearningGoalRing';

interface LearningCoachHeroProps {
  recommendedMode: LearningMode;
  recommendedCount?: number;
  goalTarget: number;
  goalDone: number;
  streak: number;
  onStart: (mode: LearningMode) => void;
  starting?: boolean;
}

const LearningCoachHero: React.FC<LearningCoachHeroProps> = ({
  recommendedMode,
  recommendedCount,
  goalTarget,
  goalDone,
  streak,
  onStart,
  starting,
}) => {
  const mode = modeById(recommendedMode);
  if (!mode) return null;

  const isRepeat = recommendedMode === 'REPEAT';
  const isNew = recommendedMode === 'NEW_WORDS';
  const count = recommendedCount;
  const word = count !== undefined ? slivWord(count) : 'слова';

  const headline = (() => {
    if (count === undefined) {
      return isRepeat
        ? 'Повтори слова, поки не забулись'
        : isNew
        ? 'Почни вивчати нові слова'
        : 'Запусти збалансовану сесію';
    }
    if (isRepeat) return `Повтори ${count} ${word}, поки не забулись`;
    if (isNew) return `Вивчи ${count} нових ${word}`;
    return `Сьогоднішня сесія — ${count} ${word}`;
  })();

  const ctaLabel = isRepeat
    ? 'Почати повторення'
    : isNew
    ? 'Почати з нових слів'
    : 'Почати сесію';

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'primary.tableBorder',
        borderRadius: 2,
        boxShadow: '0 14px 36px -28px rgba(20,20,20,0.4)',
        p: { xs: 2.5, md: '28px 30px' },
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 3, md: 4.5 }}
        divider={
          <Divider
            orientation={'vertical'}
            flexItem
            sx={{ display: { xs: 'none', md: 'block' } }}
          />
        }
        alignItems={{ xs: 'flex-start', md: 'center' }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: 'primary.main',
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: '0.09em',
              mb: 1.25,
              textTransform: 'uppercase',
            }}
          >
            {'Рекомендовано сьогодні'}
          </Typography>
          <Typography
            component={'h1'}
            sx={{
              fontSize: { xs: 22, md: 30 },
              fontWeight: 800,
              lineHeight: 1.15,
              mb: 1,
            }}
          >
            {headline}
          </Typography>
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: { xs: 14, md: 16 },
              lineHeight: 1.5,
              maxWidth: 520,
              mb: 2,
            }}
          >
            {`${mode.desc} Це найкорисніше, що можна зробити прямо зараз.`}
          </Typography>
          {!!mode.exercises.length && (
            <Box sx={{ mb: 2.5 }}>
              <LearningExerciseChips ids={mode.exercises} />
            </Box>
          )}
          <Stack
            direction={'row'}
            alignItems={'center'}
            spacing={1.75}
            sx={{ flexWrap: 'wrap', rowGap: 1 }}
          >
            <Button
              variant={'contained'}
              startIcon={<PlayArrowIcon />}
              onClick={() => onStart(recommendedMode)}
              disabled={starting}
              sx={{ height: 44, px: 2.5 }}
            >
              {ctaLabel}
            </Button>
            <Stack
              direction={'row'}
              alignItems={'center'}
              spacing={0.5}
              sx={{ color: 'text.secondary', fontSize: 13 }}
            >
              <ScheduleIcon sx={{ fontSize: 16 }} />
              <Box component={'span'}>
                {`~${mode.minutes ?? 8} хв${
                  count !== undefined ? ` · ${count} ${word}` : ''
                }`}
              </Box>
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ flex: 'none' }}>
          <LearningGoalRing
            target={goalTarget}
            done={goalDone}
            streak={streak}
          />
        </Box>
      </Stack>
    </Box>
  );
};

export default LearningCoachHero;
